import { createContext, useContext } from 'react';
import type React from 'react';

/**
 * PD-16919 — what a report page's header shows beside its title, supplied by
 * whatever wraps the report rather than by each report page.
 *
 * The reporting module's layout route provides the current report's favorite
 * star here, so all of the report pages built on ReportPageHeader get it from
 * one place without a change to any of them. Outside that layout (a category
 * page, anything that is not a report) nothing is provided and the header
 * shows nothing extra. Dashboard widgets render a report's content without
 * its page header, so they never show it.
 */
export const ReportPageHeaderSlotContext = createContext<React.ReactNode>(null);

/** The node the enclosing report layout put beside the title, or null. */
export function useReportPageHeaderSlot(): React.ReactNode {
	return useContext(ReportPageHeaderSlotContext);
}

/**
 * PD-17424 — the report page's own actions (Amounts, Download, Schedule),
 * supplied by the reporting layout route like the favorite star above, so
 * every report page built on ReportPageHeader shows them with no change to
 * the page.
 */
export const ReportPageHeaderActionsContext = createContext<React.ReactNode>(null);

/** The actions the enclosing report layout put in the header, or null. */
export function useReportPageHeaderActions(): React.ReactNode {
	return useContext(ReportPageHeaderActionsContext);
}

/**
 * PD-17424 — how money is shown on a report page: `auto` rounds the way the
 * report cards always have (tables without cents, the exact amount on
 * hover), `exact` shows every cent. Null outside a report page (dashboards,
 * every other screen), where nothing changes.
 *
 * Tables and number tiles read this so they redraw when it changes; report
 * money formatters read the same choice from the reporting module.
 */
export const ReportAmountsContext = createContext<'auto' | 'exact' | null>(null);

export function useReportAmounts(): 'auto' | 'exact' | null {
	return useContext(ReportAmountsContext);
}

/**
 * PD-17424 — where a report page's CSV export goes when the page sits under
 * the reporting layout: the header's Download menu offers it as "CSV", and
 * the page's own Export CSV button is not drawn. The export itself is
 * unchanged (the rows on screen, or the server's streamed file when the
 * screen holds only a page of them).
 */
export interface ReportCsvExportRegistryModel {
	/** Offers an export; returns the function that withdraws it. */
	register: (exportCsv: () => void) => () => void;
}

export const ReportCsvExportRegistryContext =
	createContext<ReportCsvExportRegistryModel | null>(null);

export function useReportCsvExportRegistry(): ReportCsvExportRegistryModel | null {
	return useContext(ReportCsvExportRegistryContext);
}

/**
 * PD-17539 — the breadcrumb steps ABOVE the page on screen (Reports, then the
 * report's category), supplied by the reporting layout route. ReportPageHeader
 * adds the page's own title as the last step. Null outside that route, so a
 * dashboard widget or any other screen shows no breadcrumb.
 */
export interface ReportPageBreadcrumbStepModel {
	label: string;
	to: string;
}

export const ReportPageBreadcrumbContext = createContext<ReadonlyArray<ReportPageBreadcrumbStepModel> | null>(null);

export function useReportPageBreadcrumb(): ReadonlyArray<ReportPageBreadcrumbStepModel> | null {
	return useContext(ReportPageBreadcrumbContext);
}

/**
 * PD-17425 — the company's own name for the open report, when it has one.
 * ReportPageHeader shows it in place of the title the page passes.
 */
export const ReportPageTitleContext = createContext<string | null>(null);

export function useReportPageTitleOverride(): string | null {
	return useContext(ReportPageTitleContext);
}
